import { Sparkline } from '@/components/dashboard/Sparkline'
import { toNumber } from '@/lib/decimal'
import { formatContractHours, formatHours } from '@/lib/format'
import { useUtilizationReport } from '@/lib/time-queries'
import type { DayTotal, UtilizationSummary } from '@/lib/types'
import { weekMetaFromId } from '@/lib/week'
import { UtilizationBar } from './UtilizationBar'

interface UtilizationStripProps {
  personId?: string
  weekId: string
  utilization: UtilizationSummary
  dailyTotals: DayTotal[]
}

function breakdown(dailyTotals: DayTotal[]): string[] {
  const vacation = dailyTotals.filter((d) => d.dayType === 'vacation').length
  const holiday = dailyTotals.filter((d) => d.dayType === 'holiday').length
  const parts: string[] = []
  if (vacation > 0) parts.push(`${vacation} vakantiedag${vacation === 1 ? '' : 'en'}`)
  if (holiday > 0) parts.push(`${holiday} feestdag${holiday === 1 ? '' : 'en'}`)
  return parts
}

export function UtilizationStrip({ personId, weekId, utilization, dailyTotals }: UtilizationStripProps) {
  const anchor = weekMetaFromId(weekId).days[0].iso
  const report = useUtilizationReport({ personId, period: 'week', anchor })
  const series = (report.data?.series ?? []).map((point) => point.percentage ?? 0)

  const expected = utilization.expectedHours
  const wouldBeWorkdays = dailyTotals.filter((d) => d.dayType === 'workday' || d.dayType === 'vacation')
  const fullVacation =
    utilization.contractHours != null &&
    wouldBeWorkdays.length > 0 &&
    wouldBeWorkdays.every((d) => d.dayType === 'vacation')
  const parts = breakdown(dailyTotals)
  const contractTitle =
    utilization.contractHours != null && expected != null
      ? [`Verwacht ${formatHours(toNumber(expected))} u van ${formatContractHours(toNumber(utilization.contractHours))} u contract`, ...parts].join(' · ')
      : undefined

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-card border border-hairline bg-surface px-4 py-3 shadow-card">
      <div className="min-w-[160px]">
        <div className="label-caps">Declarabel deze week</div>
        <div className="mt-1.5 flex items-baseline gap-3">
          <UtilizationBar
            hours={utilization.hours}
            contractHours={utilization.contractHours}
            percentage={utilization.percentage}
            contractTitle={contractTitle}
            className="min-w-[110px]"
          />
          {fullVacation ? (
            <span className="text-sm font-medium text-ink-soft">Vakantie</span>
          ) : utilization.percentage != null ? (
            <span className="num text-sm text-ink-soft">{utilization.percentage}%</span>
          ) : null}
        </div>
      </div>
      {series.length >= 2 ? (
        <div className="flex flex-col items-end gap-1">
          <span className="label-caps">8 weken</span>
          <Sparkline data={series} />
        </div>
      ) : null}
    </div>
  )
}
